import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import Label from 'components/form/Label/Label';
import React from 'react';
import { Image } from 'components/Image/Image';
import classnames from 'classnames';
import { useSeenModulePixel } from 'hook/helper.hook';
import { isNonControlVariationLoadedAndEnabled } from 'helpers/optimizely.helper';
import { isPremium, isFree, isPlus, isBuilderUser } from 'helpers/userHelper';
import './CreditToolsModule.scss';
import { useFlag } from 'hook/flag.hook';
import { SUBSCRIPTION_VERTICAL } from 'types/mrph.vertical.constants';
import { getViewModuleTrackingAttributes } from 'helpers/tracking.helpers';
import { CREDIT_TOOLS } from 'types/morph.pagePosition.constants';
import OText from 'components/CText/OText';
import {
  getCreditToolsArrayTiles,
  csTilesOrder,
} from 'customHelpersPath/creditTools.helper';
import { Row } from 'components/Containers/Row/Row';
import OfferCarousel from 'components/Fragments/OfferCarousel/OfferCarousel';
import classNames from 'classnames';

const CreditToolsImage = props => {
  const { icon, disabled } = props;
  return (
    <FlexContainer className={'format20'}>
      <Image className={disabled ? 'disabledlockIcon' : icon} />
    </FlexContainer>
  );
};

const CreditTools = props => {
  const {
    isDesktop,
    title,
    notification,
    secondLabel,
    highlighted,
    isSuccess,
    isAlert,
    icon,
    disabled,
    onClick,
    fontColorOverRide,
    isTop,
    isEightTools,
  } = props;

  const isEightToolsAndIsDesktop = isEightTools && isDesktop;

  const sectionContainerClass = classnames(
    'flex-container flex-column grayShadow16 margt12 border-rad8px pointer format24 margb0 m-margb4 gap-2 padTB16',
    {
      'width-132px height-130px': isDesktop,
      'm-width-160px': !isDesktop,
      'padlr10 height130px': isEightToolsAndIsDesktop,
      'padlr12 height160px': !isEightToolsAndIsDesktop,
      bgYellow050: highlighted,
      system_neutral_n000: !highlighted,
    },
  );
  const titleClass = classnames('bold format180', {
    fontGray075: disabled,
    system_neutral_n400_text: !disabled,
    'text-13': isEightToolsAndIsDesktop,
    'text-14': !isEightToolsAndIsDesktop,
  });

  const notificationClass = classnames(
    'flex-container flex-column height100 txtGrey400',
    fontColorOverRide,
    {
      fontGray075: disabled,
      fontRed075: isAlert && !disabled,
      green100: isSuccess && !disabled,
      'justify-end': !secondLabel && !isTop,
      'text-11': isEightToolsAndIsDesktop,
      'text-12': !isEightToolsAndIsDesktop,
    },
  );

  const secondLabelClass = classnames('fontBlue400 bold', {
    'text-8': isEightToolsAndIsDesktop,
    'text-10': !isEightToolsAndIsDesktop,
  });

  const spanSecondLabelClass = classnames('border-rad10px bgBlue1 padtb6', {
    padlr4: isEightToolsAndIsDesktop,
    padlr10: !isEightToolsAndIsDesktop,
  });

  return (
    <FlexContainer className={sectionContainerClass} onClick={onClick}>
      <FlexContainer className={'justify-between flex-grow-1 gap-2'}>
        <Label className={titleClass}>{title}</Label>
        <CreditToolsImage icon={icon} disabled={disabled} />
      </FlexContainer>
      <FlexContainer className={'flex-column height100 gap-2 justify-between'}>
        <Label className={notificationClass}>
          <OText text={notification} />
        </Label>
        {secondLabel && (
          <Label className={secondLabelClass}>
            <span className={spanSecondLabelClass}>{secondLabel}</span>
          </Label>
        )}
      </FlexContainer>
    </FlexContainer>
  );
};

const CreditToolsGrid = props => {
  const { creditToolsArray, scoreSimulatorEnabled, onClickCreditTools } = props;
  const firstThreeTools = creditToolsArray.slice(0, 4);
  const secondThreeTools = creditToolsArray.slice(4, 8);
  const thirdThreeTools = creditToolsArray.slice(8, creditToolsArray?.length);
  const isEightTools =
    creditToolsArray?.length === scoreSimulatorEnabled ? 9 : 8;

  return (
    <FlexContainer className={'flex-column m-hide'}>
      <FlexContainer className={'gap-12 pad4'}>
        {firstThreeTools?.map((tool, index) => (
          <CreditTools
            key={'firstDesktopRowTools' + index}
            isDesktop={true}
            title={tool?.title}
            notification={tool?.notification}
            secondLabel={tool?.secondLabel}
            highlighted={tool?.highlighted}
            isAlert={tool?.isAlert}
            isSuccess={tool?.isSuccess}
            icon={tool?.icon}
            disabled={tool?.disabled}
            onClick={() => onClickCreditTools({ tileInfo: tool })}
            fontColorOverRide={tool?.fontColorOverRide}
            isTop={tool?.isTop}
            isEightTools={isEightTools}
          />
        ))}
      </FlexContainer>
      <FlexContainer className={'gap-12 pad4'}>
        {secondThreeTools?.map((tool, index) => (
          <CreditTools
            key={'secondDesktopRowTools' + index}
            isDesktop={true}
            title={tool?.title}
            notification={tool?.notification}
            secondLabel={tool?.secondLabel}
            highlighted={tool?.highlighted}
            isAlert={tool?.isAlert}
            isSuccess={tool?.isSuccess}
            icon={tool?.icon}
            disabled={tool?.disabled}
            onClick={() => onClickCreditTools({ tileInfo: tool })}
            fontColorOverRide={tool?.fontColorOverRide}
            isTop={tool?.isTop}
            isEightTools={isEightTools}
          />
        ))}
      </FlexContainer>
      <FlexContainer className={'gap-12 pad4'}>
        {thirdThreeTools?.map((tool, index) => (
          <CreditTools
            key={'thirdDesktopRowTools' + index}
            isDesktop={true}
            title={tool?.title}
            notification={tool?.notification}
            secondLabel={tool?.secondLabel}
            highlighted={tool?.highlighted}
            isAlert={tool?.isAlert}
            isSuccess={tool?.isSuccess}
            icon={tool?.icon}
            disabled={tool?.disabled}
            onClick={() => onClickCreditTools({ tileInfo: tool })}
            fontColorOverRide={tool?.fontColorOverRide}
            isTop={tool?.isTop}
            isEightTools={isEightTools}
          />
        ))}
      </FlexContainer>
    </FlexContainer>
  );
};

const CreditToolsCarousel = props => {
  const { creditToolsArray, onClickCreditTools } = props;

  return (
    <Row fullWidth type="carousel" className="m-hide">
      <OfferCarousel type="creditToolsCarousel">
        {creditToolsArray?.map((tool, index) => (
          <div className="margR10 margL6 margB4">
            <CreditTools
              key={'creditToolsCarousel' + index}
              isDesktop={true}
              title={tool?.title}
              notification={tool?.notification}
              secondLabel={tool?.secondLabel}
              highlighted={tool?.highlighted}
              isAlert={tool?.isAlert}
              isSuccess={tool?.isSuccess}
              icon={tool?.icon}
              disabled={tool?.disabled}
              onClick={() => onClickCreditTools({ tileInfo: tool })}
              fontColorOverRide={tool?.fontColorOverRide}
              isTop={tool?.isTop}
              isEightTools={true}
            />
          </div>
        ))}
      </OfferCarousel>
    </Row>
  );
};

const CreditToolsDesktopView = props => {
  const { isDesktopCarouselView } = props;

  return isDesktopCarouselView ? (
    <CreditToolsCarousel {...props} />
  ) : (
    <CreditToolsGrid {...props} />
  );
};

const CreditToolsMobileView = props => {
  const { creditToolsArray, onClickCreditTools } = props;

  return (
    <FlexContainer className={'flex-column d-hide horizontalScrollBar'}>
      <FlexContainer className={`m-formatFitContent gap-12 padlr4`}>
        {creditToolsArray?.map((tool, index) => (
          <CreditTools
            key={'CarosuelMobileRowTools' + index}
            isDesktop={false}
            title={tool?.title}
            notification={tool?.notification}
            secondLabel={tool?.secondLabel}
            highlighted={tool?.highlighted}
            isAlert={tool?.isAlert}
            isSuccess={tool?.isSuccess}
            icon={tool?.icon}
            disabled={tool?.disabled}
            onClick={() => onClickCreditTools({ tileInfo: tool })}
            fontColorOverRide={tool?.fontColorOverRide}
            isTop={tool?.isTop}
          />
        ))}
      </FlexContainer>
    </FlexContainer>
  );
};

const rentReportingMessageMapping = {
  RESET_MEMBER: { message: 'Get Started Now', color: 'fontBlue100' },
  TRADELINE_CLOSED: { message: 'Get Started Now', color: 'fontBlue100' },
  ACCOUNT_CLOSED_TRADELINE_REMOVED: {
    message: 'Get Started Now',
    color: 'fontBlue100',
  },
  PREMIUM_ACCOUNT_CANCELLED: {
    message: 'Get Started Now',
    color: 'fontBlue100',
  },
  TRADELINE_TRANSFERRED: { message: 'Get Started Now', color: 'fontBlue100' },
  READY_FOR_VERIFICATION: {
    message: 'Verify your rental information',
    color: 'fontOrange100',
  },
  PUSHED_TO_SECONDARY_VERIFICATION: {
    message: 'Verify your rental information',
    color: 'fontOrange100',
  },
  VOR_SENT: {
    message: 'Verify your rental information',
    color: 'fontOrange100',
  },
  ESCALATED_VOR: {
    message: 'Verify your rental information',
    color: 'fontOrange100',
  },
  CALL_TO_VERIFY: {
    message: 'Verify your rental information',
    color: 'fontOrange100',
  },
  BANK_ACCOUNT_NOT_LINKED: {
    message: 'Link Your Bank',
    color: 'fontGray200',
  },
  LANDLORD_DETAILS_NEEDED: {
    message: 'Landlord Details Needed',
    color: 'fontRed200',
  },
  VOR_REJECTED: {
    message: 'Unable to verify rental details',
    color: 'fontRed200',
  },
  RENTAL_DETAILS_NEEDED: {
    message: 'Rental Details Needed',
    color: 'fontRed200',
  },
  BANK_ACCOUNT_LINKED: {
    message: 'Identify Payments',
    color: 'fontRed200',
  },
  IDENTIFY_PAYMENTS: {
    message: 'Identify Payments',
    color: 'fontRed200',
  },
  WAITING_FOR_INFORMATION: {
    message: 'Upload Your Document',
    color: 'fontRed200',
  },
  UNABLE_TO_VERIFY: {
    message: 'Unable to verify rental details',
    color: 'fontRed200',
  },
};

const CreditToolsModuleView = props => {
  const {
    userInfo,
    cashAccount,
    history,
    newAlertsCreditMonitoring,
    bureauScoreDetails,
    newCsidAlerts,
    isDesktopCarouselView,
    scoreSimulatorEnabled,
    className,
    onClickCreditTools,
  } = props;

  const isPremiumUser = isPremium(userInfo);
  const isPlusUser = isPlus(userInfo);
  const isFreeUser = isFree(userInfo);
  const isCBuilderUser = isBuilderUser(cashAccount);

  const plusLaunchWebFlag = useFlag('pluslaunch_web');

  const isPlusLaunchWebFlagEnabled =
    isNonControlVariationLoadedAndEnabled(plusLaunchWebFlag);

  const rentReportingMapped = userInfo?.rentReporting
    ? rentReportingMessageMapping[userInfo?.rentReporting?.status]
    : { message: 'Get Started Now', color: 'fontBlue100' };

  const trackingAtr = getViewModuleTrackingAttributes({
    pagePosition: CREDIT_TOOLS,
    modulePosition: props.modulePosition,
    vertical: SUBSCRIPTION_VERTICAL,
  });
  const seenModuleTracking = useSeenModulePixel(trackingAtr);

  const creditToolsArrayTiles = getCreditToolsArrayTiles({
    scoreSimulatorEnabled,
    isPlusUser,
    bureauScoreDetails,
    rentReportingMapped,
    isPremiumUser,
    userInfo,
    newCsidAlerts,
    newAlertsCreditMonitoring,
    csTilesOrder,
    isCBuilderUser,
  });

  const creditToolsArrayEightTiles =
    creditToolsArrayTiles?.creditToolsArrayEightTiles;

  const creditToolsArraySixTiles =
    creditToolsArrayTiles?.creditToolsArraySixTiles;

  let creditToolsArray;
  let titlePill = 'Available exclusively for Sesame+';

  if (isFreeUser) {
    creditToolsArray = isPlusLaunchWebFlagEnabled
      ? creditToolsArrayEightTiles
      : creditToolsArraySixTiles;

    if (isPlusLaunchWebFlagEnabled) {
      creditToolsArray = creditToolsArrayEightTiles;
    } else {
      creditToolsArray = creditToolsArraySixTiles;
      titlePill = 'Available exclusively for Premium';
    }
  } else {
    if (isPlusUser) {
      creditToolsArray = creditToolsArrayEightTiles;
    } else if (isPremiumUser) {
      creditToolsArray = creditToolsArraySixTiles;
      titlePill = 'Available exclusively for Premium';
    }
  }

  return (
    <FlexContainer
      className={classNames(
        'flex-column creditToolsModule d-marglr12 m-margt8 m-margb0 m-format100 padR14 m-padr0',
        { margt40: !isDesktopCarouselView },
        className,
      )}
    >
      {seenModuleTracking}
      <FlexContainer className={'justify-between width100'}>
        <Label
          type="H4"
          typeHeader={'h4'}
          className={classNames(
            'align-self-center margB0 whitespace-nowrap m-text-12',
            { 'text-20': isDesktopCarouselView },
          )}
        >
          Credit Tools
        </Label>
        <Label className="text-10 bold margt10">{titlePill}</Label>
      </FlexContainer>
      <CreditToolsDesktopView
        creditToolsArray={creditToolsArray}
        history={history}
        userInfo={userInfo}
        cashAccount={cashAccount}
        onClickCreditTools={onClickCreditTools}
        isDesktopCarouselView={isDesktopCarouselView}
      />
      <CreditToolsMobileView
        creditToolsArray={creditToolsArray}
        history={history}
        userInfo={userInfo}
        cashAccount={cashAccount}
        isPlusLaunchWebFlagEnabled={isPlusLaunchWebFlagEnabled}
        onClickCreditTools={onClickCreditTools}
      />
    </FlexContainer>
  );
};

export default CreditToolsModuleView;
